import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { UI_BUILD_INFO } from '../generatedBuildInfo';
import { FiCalendar, FiLogOut, FiUser, FiBell, FiRefreshCw, FiHome, FiArrowUp, FiArrowDown, FiEye, FiEyeOff, FiFolder, FiCheckSquare } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const DEFAULT_DASHBOARD_WIDGET_ORDER = [
  'welcome',
  'upcoming_events',
  'quick_actions',
  'calendar_connections',
  'notification_status',
  'chats',
  'bookmarks',
  'future_notes'
];

const DASHBOARD_WIDGET_LABELS = {
  welcome: 'Welcome',
  upcoming_events: 'Upcoming Events',
  quick_actions: 'Quick Actions',
  calendar_connections: 'Calendar Connections',
  notification_status: 'Notifications',
  chats: 'Chats',
  bookmarks: 'Bookmarks',
  future_notes: 'FutureNotes'
};

const BOOKMARK_DEFAULT_OPEN_MODE = 'new_tab';
const BOOKMARK_ROOT_FOLDER_PATTERN = /^favorites bar(?:\s*\/\s*)?/i;

const normalizeBookmarkDefaultOpenMode = (value) => (
  value === 'same_tab' ? 'same_tab' : 'new_tab'
);

const normalizeBookmarkOverrideOpenMode = (value) => (
  value === 'same_tab' || value === 'new_tab' ? value : 'inherit'
);

const normalizeBookmarkFolderPath = (folderPath = '') => {
  const normalized = String(folderPath || '')
    .trim()
    .replace(BOOKMARK_ROOT_FOLDER_PATTERN, '')
    .trim();

  return normalized || 'Unsorted';
};

const normalizeFolderPathArray = (items = []) => {
  const next = [];
  const seen = new Set();

  for (const item of items) {
    const normalized = normalizeBookmarkFolderPath(item);
    if (!seen.has(normalized)) {
      next.push(normalized);
      seen.add(normalized);
    }
  }

  return next;
};

const normalizeFolderPathMap = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [folderPath, entryValue]) => {
    accumulator[normalizeBookmarkFolderPath(folderPath)] = entryValue;
    return accumulator;
  }, {});
};

const getBookmarkPreferences = (user) => ({
  defaultOpenMode: normalizeBookmarkDefaultOpenMode(user?.dashboardPreferences?.bookmarks?.defaultOpenMode),
  folderOverrides: Array.isArray(user?.dashboardPreferences?.bookmarks?.folderOverrides)
    ? user.dashboardPreferences.bookmarks.folderOverrides
        .map((override) => ({
          folderPath: normalizeBookmarkFolderPath(override?.folderPath),
          openMode: normalizeBookmarkOverrideOpenMode(override?.openMode)
        }))
        .filter((override) => override.folderPath && override.openMode !== 'inherit')
    : []
});

const getBookmarkLayoutKey = (userId) => `futurenote.bookmarks.layout.${userId}`;
const getBookmarkCacheKey = (userId) => `futurenote.bookmarks.cache.${userId}`;

const readLocalJson = (key, fallback) => {
  if (typeof window === 'undefined' || !key) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeLocalJson = (key, value) => {
  if (typeof window === 'undefined' || !key) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore local storage write failures.
  }
};

const normalizeDashboardWidgetOrder = (order = []) => {
  const nextOrder = [];
  const seen = new Set();

  for (const widgetId of order) {
    if (DEFAULT_DASHBOARD_WIDGET_ORDER.includes(widgetId) && !seen.has(widgetId)) {
      nextOrder.push(widgetId);
      seen.add(widgetId);
    }
  }

  for (const widgetId of DEFAULT_DASHBOARD_WIDGET_ORDER) {
    if (!seen.has(widgetId)) {
      nextOrder.push(widgetId);
    }
  }

  return nextOrder;
};

const normalizeHiddenWidgets = (hiddenWidgets = []) => {
  const nextHidden = [];
  const seen = new Set();

  for (const widgetId of hiddenWidgets) {
    if (DEFAULT_DASHBOARD_WIDGET_ORDER.includes(widgetId) && !seen.has(widgetId)) {
      nextHidden.push(widgetId);
      seen.add(widgetId);
    }
  }

  return nextHidden;
};

const getBookmarkFolderSegments = (folderPath = '') => {
  const segments = normalizeBookmarkFolderPath(folderPath)
    .split(/\s*\/\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return segments.length > 0 ? segments : ['Unsorted'];
};

const getBookmarkFolderLabel = (folderPath = '') => {
  const segments = getBookmarkFolderSegments(folderPath);
  return segments[segments.length - 1] || 'Unsorted';
};

const Settings = () => {
  const { user, logout, updateUser, linkProvider } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('profile');
  const location = useLocation();
  const userId = user?.id || '';
  const bookmarkLayoutKey = useMemo(() => (userId ? getBookmarkLayoutKey(userId) : ''), [userId]);
  const bookmarkCacheKey = useMemo(() => (userId ? getBookmarkCacheKey(userId) : ''), [userId]);
  const [dashboardWidgetOrder, setDashboardWidgetOrder] = useState(DEFAULT_DASHBOARD_WIDGET_ORDER);
  const [hiddenDashboardWidgets, setHiddenDashboardWidgets] = useState([]);
  const [bookmarkFolderOrder, setBookmarkFolderOrder] = useState([]);
  const [hiddenBookmarkFolders, setHiddenBookmarkFolders] = useState({});

  // Fetch notification preferences
  const { data: preferences, isLoading: prefsLoading } = useQuery(
    'notificationPreferences',
    async () => {
      const response = await api.get('/notifications/preferences');
      return response.data.preferences;
    }
  );

  const [preferenceForm, setPreferenceForm] = useState(null);

  useEffect(() => {
    if (preferences) {
      setPreferenceForm(preferences);
    }
  }, [preferences]);

  useEffect(() => {
    const nextOrder = normalizeDashboardWidgetOrder(user?.dashboardPreferences?.widgetOrder || DEFAULT_DASHBOARD_WIDGET_ORDER);
    const nextHidden = normalizeHiddenWidgets(user?.dashboardPreferences?.hiddenWidgets || []);
    setDashboardWidgetOrder(nextOrder);
    setHiddenDashboardWidgets(nextHidden);
  }, [user?.dashboardPreferences]);

  useEffect(() => {
    if (!userId || !bookmarkLayoutKey) {
      setBookmarkFolderOrder([]);
      setHiddenBookmarkFolders({});
      return;
    }

    const storedLayout = readLocalJson(bookmarkLayoutKey, null);
    setBookmarkFolderOrder(Array.isArray(storedLayout?.folderOrder) ? normalizeFolderPathArray(storedLayout.folderOrder) : []);
    setHiddenBookmarkFolders(
      storedLayout?.hiddenFolders && typeof storedLayout.hiddenFolders === 'object'
        ? normalizeFolderPathMap(storedLayout.hiddenFolders)
        : {}
    );
  }, [bookmarkLayoutKey, userId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const linked = params.get('linked');
    const capability = params.get('capability');
    const error = params.get('error');
    if (requestedTab && ['profile', 'home', 'notifications', 'connections'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    } else if (linked || error) {
      setActiveTab('connections');
    }
    if (linked) {
      const capabilityLabel = capability === 'tasks' ? 'tasks' : 'calendar';
      toast.success(`${linked} ${capabilityLabel} connected successfully!`);
      queryClient.invalidateQueries('oauthAccounts');
      queryClient.invalidateQueries('calendarStatus');
      queryClient.invalidateQueries('tasks');
    }
    if (error) {
      toast.error('Failed to link account. Please try again.');
    }
  }, [location.search, queryClient]);

  const { data: accounts = [], isLoading: accountsLoading } = useQuery(
    'oauthAccounts',
    async () => {
      const response = await api.get('/accounts');
      return response.data.accounts || [];
    }
  );

  const accountsByProvider = useMemo(() => {
    return accounts.reduce((acc, account) => {
      const list = acc[account.provider] || [];
      list.push(account);
      acc[account.provider] = list;
      return acc;
    }, { google: [], microsoft: [] });
  }, [accounts]);

  const { data: bookmarkData } = useQuery(
    'bookmarks',
    async () => {
      const response = await api.get('/bookmarks?limit=500');
      return response.data;
    },
    {
      enabled: !!userId
    }
  );

  const { data: buildInfo } = useQuery(
    'buildInfo',
    async () => {
      const response = await api.get('/build-info');
      return response.data.backend || null;
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  );

  const bookmarkCache = useMemo(
    () => (bookmarkCacheKey ? readLocalJson(bookmarkCacheKey, null) : null),
    [bookmarkCacheKey]
  );

  const activeBookmarkData = bookmarkData || bookmarkCache;
  const bookmarkPreferences = useMemo(() => getBookmarkPreferences(user), [user]);
  const bookmarkFolderOverrideMap = useMemo(
    () => new Map(bookmarkPreferences.folderOverrides.map((override) => [override.folderPath, override.openMode])),
    [bookmarkPreferences.folderOverrides]
  );

  const allBookmarkFolderItems = useMemo(() => {
    const counts = new Map();
    for (const bookmark of activeBookmarkData?.bookmarks || []) {
      const folderPath = normalizeBookmarkFolderPath(bookmark.folderPath);
      counts.set(folderPath, (counts.get(folderPath) || 0) + 1);
    }

    const orderMap = new Map(bookmarkFolderOrder.map((folderPath, index) => [folderPath, index]));

    return [...counts.entries()]
      .map(([folderPath, count]) => ({
        folderPath,
        label: getBookmarkFolderLabel(folderPath),
        pathLabel: getBookmarkFolderSegments(folderPath).join(' / '),
        count,
        openMode: bookmarkFolderOverrideMap.get(folderPath) || 'inherit'
      }))
      .sort((left, right) => {
        const leftIndex = orderMap.has(left.folderPath) ? orderMap.get(left.folderPath) : Number.MAX_SAFE_INTEGER;
        const rightIndex = orderMap.has(right.folderPath) ? orderMap.get(right.folderPath) : Number.MAX_SAFE_INTEGER;
        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }
        return left.folderPath.localeCompare(right.folderPath);
      });
  }, [activeBookmarkData, bookmarkFolderOrder, bookmarkFolderOverrideMap]);

  const bookmarkPreferenceItems = useMemo(() => (
    [...(activeBookmarkData?.bookmarks || [])]
      .sort((left, right) => {
        const leftFolder = normalizeBookmarkFolderPath(left.folderPath);
        const rightFolder = normalizeBookmarkFolderPath(right.folderPath);
        if (leftFolder !== rightFolder) {
          return leftFolder.localeCompare(rightFolder);
        }
        return (left.title || '').localeCompare(right.title || '');
      })
      .map((bookmark) => ({
        ...bookmark,
        folderPath: normalizeBookmarkFolderPath(bookmark.folderPath),
        bookmarkOpenMode: normalizeBookmarkOverrideOpenMode(bookmark.openMode),
        folderOpenMode: bookmarkFolderOverrideMap.get(normalizeBookmarkFolderPath(bookmark.folderPath)) || 'inherit'
      }))
  ), [activeBookmarkData, bookmarkFolderOverrideMap]);

  const bookmarkCountByFolder = useMemo(() => {
    const counts = {};
    const bookmarks = activeBookmarkData?.bookmarks || [];

    for (const bookmark of bookmarks) {
      const folderPath = normalizeBookmarkFolderPath(bookmark.folderPath);
      counts[folderPath] = (counts[folderPath] || 0) + 1;
    }

    return counts;
  }, [activeBookmarkData]);

  const hiddenBookmarkFolderItems = useMemo(() => {
    const hiddenEntries = Object.entries(hiddenBookmarkFolders).filter(([, isHidden]) => !!isHidden);
    const orderMap = new Map(bookmarkFolderOrder.map((folderPath, index) => [folderPath, index]));

    return hiddenEntries
      .map(([folderPath]) => ({
        folderPath,
        label: getBookmarkFolderLabel(folderPath),
        pathLabel: getBookmarkFolderSegments(folderPath).join(' / '),
        count: bookmarkCountByFolder[folderPath] || 0
      }))
      .sort((left, right) => {
        const leftIndex = orderMap.has(left.folderPath) ? orderMap.get(left.folderPath) : Number.MAX_SAFE_INTEGER;
        const rightIndex = orderMap.has(right.folderPath) ? orderMap.get(right.folderPath) : Number.MAX_SAFE_INTEGER;
        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }
        return left.folderPath.localeCompare(right.folderPath);
      });
  }, [bookmarkCountByFolder, bookmarkFolderOrder, hiddenBookmarkFolders]);

  const updateBookmarkPreferencesMutation = useMutation(
    async (payload) => {
      const response = await api.put('/bookmarks/preferences', payload);
      return response.data.preferences;
    },
    {
      onSuccess: (preferences) => {
        updateUser({
          ...user,
          dashboardPreferences: {
            ...(user?.dashboardPreferences || {}),
            bookmarks: preferences
          }
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update bookmark settings');
      }
    }
  );

  const updateBookmarkItemPreferenceMutation = useMutation(
    async ({ bookmarkId, openMode }) => {
      const response = await api.put(`/bookmarks/${bookmarkId}/preferences`, { openMode });
      return response.data.bookmark;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bookmarks');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update bookmark preference');
      }
    }
  );

  // Update notification preferences mutation
  const updatePreferencesMutation = useMutation(
    async (newPreferences) => {
      const response = await api.put('/notifications/preferences', newPreferences);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Notification preferences updated successfully!');
        if (user) {
          updateUser({
            ...user,
            notificationPreferences: data.preferences
          });
        }
        queryClient.invalidateQueries('notificationPreferences');
      },
      onError: (error) => {
        toast.error(`Failed to update preferences: ${error.response?.data?.message || 'Unknown error'}`);
      },
    }
  );

  // Update phone number mutation
  const updatePhoneMutation = useMutation(
    async (phoneNumber) => {
      const response = await api.put('/notifications/phone', { phoneNumber });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Phone number updated successfully!');
        updateUser({ ...user, phoneNumber: data.phoneNumber });
      },
      onError: (error) => {
        toast.error(`Failed to update phone number: ${error.response?.data?.message || 'Unknown error'}`);
      },
    }
  );

  // Test notification mutation
  const testNotificationMutation = useMutation(
    async (type) => {
      const response = await api.post('/notifications/test', { type });
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('Test notification sent successfully!');
        } else {
          toast.error('Some notifications failed to send');
        }
      },
      onError: (error) => {
        toast.error(`Failed to send test notification: ${error.response?.data?.message || 'Unknown error'}`);
      },
    }
  );

  const resyncAllMutation = useMutation(
    async () => {
      const response = await api.post('/calendar/resync');
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Resync completed and notifications reset');
        queryClient.invalidateQueries('upcomingEvents');
        queryClient.invalidateQueries('calendarStatus');
        queryClient.invalidateQueries('notificationPreferences');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Resync failed';
        toast.error(message);
      }
    }
  );

  // Disconnect OAuth mutation
  const disconnectOAuthMutation = useMutation(
    async (accountId) => {
      const response = await api.delete(`/accounts/${accountId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Account disconnected successfully!');
        queryClient.invalidateQueries('oauthAccounts');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Unknown error';
        toast.error(`Failed to disconnect account: ${message}`);
      },
    }
  );

  const dashboardPreferencesMutation = useMutation(
    async ({ widgetOrder, hiddenWidgets }) => {
      const response = await api.put('/users/dashboard-preferences', {
        widgetOrder,
        hiddenWidgets
      });
      return response.data.user;
    },
    {
      onSuccess: (nextUser) => {
        updateUser(nextUser);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update home page settings');
      }
    }
  );

  const handleLogout = () => {
    logout();
  };

  const handleTestNotification = (type) => {
    testNotificationMutation.mutate(type);
  };

  const handleQuietHoursChange = (patch) => {
    setPreferenceForm((prev) => {
      const currentQuietHours = prev?.quietHours || {};
      const nextQuietHours = {
        enabled: currentQuietHours.enabled || false,
        start: currentQuietHours.start || '22:00',
        end: currentQuietHours.end || '07:00',
        ...patch
      };

      if (nextQuietHours.enabled && nextQuietHours.start === nextQuietHours.end) {
        toast.error('Quiet hours start and end cannot be the same');
        return prev;
      }

      const next = {
        ...(prev || {}),
        quietHours: nextQuietHours
      };

      updatePreferencesMutation.mutate(next);
      return next;
    });
  };

  const handleDisconnectOAuth = (accountId, providerLabel) => {
    if (window.confirm(`Are you sure you want to disconnect ${providerLabel}?`)) {
      disconnectOAuthMutation.mutate(accountId);
    }
  };

  const persistDashboardPreferences = (nextOrder, nextHidden) => {
    setDashboardWidgetOrder(nextOrder);
    setHiddenDashboardWidgets(nextHidden);
    dashboardPreferencesMutation.mutate({
      widgetOrder: nextOrder,
      hiddenWidgets: nextHidden
    });
  };

  const handleMoveWidget = (widgetId, direction) => {
    const currentIndex = dashboardWidgetOrder.indexOf(widgetId);
    if (currentIndex === -1) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= dashboardWidgetOrder.length) {
      return;
    }

    const nextOrder = [...dashboardWidgetOrder];
    const [movedWidget] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(targetIndex, 0, movedWidget);
    persistDashboardPreferences(nextOrder, hiddenDashboardWidgets);
  };

  const handleSetWidgetVisibility = (widgetId, shouldShow) => {
    const nextHidden = shouldShow
      ? hiddenDashboardWidgets.filter((id) => id !== widgetId)
      : hiddenDashboardWidgets.includes(widgetId)
        ? hiddenDashboardWidgets
        : [...hiddenDashboardWidgets, widgetId];

    persistDashboardPreferences(dashboardWidgetOrder, nextHidden);
  };

  const persistBookmarkLayout = (nextHiddenFolders) => {
    const storedLayout = readLocalJson(bookmarkLayoutKey, {});
    const nextLayout = {
      folderOrder: Array.isArray(storedLayout?.folderOrder) ? storedLayout.folderOrder : bookmarkFolderOrder,
      itemOrder: storedLayout?.itemOrder && typeof storedLayout.itemOrder === 'object' ? storedLayout.itemOrder : {},
      collapsedFolders: storedLayout?.collapsedFolders && typeof storedLayout.collapsedFolders === 'object' ? storedLayout.collapsedFolders : {},
      folderViewModes: storedLayout?.folderViewModes && typeof storedLayout.folderViewModes === 'object' ? storedLayout.folderViewModes : {},
      folderZoomLevels: storedLayout?.folderZoomLevels && typeof storedLayout.folderZoomLevels === 'object' ? storedLayout.folderZoomLevels : {},
      folderShowText: storedLayout?.folderShowText && typeof storedLayout.folderShowText === 'object' ? storedLayout.folderShowText : {},
      hiddenFolders: nextHiddenFolders
    };

    setHiddenBookmarkFolders(nextHiddenFolders);
    writeLocalJson(bookmarkLayoutKey, nextLayout);
  };

  const handleShowHiddenBookmarkFolder = (folderPath) => {
    persistBookmarkLayout({
      ...hiddenBookmarkFolders,
      [folderPath]: false
    });
  };

  const handleShowAllHiddenBookmarkFolders = () => {
    const nextHidden = { ...hiddenBookmarkFolders };
    for (const item of hiddenBookmarkFolderItems) {
      nextHidden[item.folderPath] = false;
    }
    persistBookmarkLayout(nextHidden);
  };

  const handleUpdateBookmarkDefaultOpenMode = (openMode) => {
    updateBookmarkPreferencesMutation.mutate({
      defaultOpenMode: normalizeBookmarkDefaultOpenMode(openMode),
      folderOverrides: bookmarkPreferences.folderOverrides
    });
  };

  const handleUpdateBookmarkFolderOpenMode = (folderPath, openMode) => {
    const normalizedFolderPath = normalizeBookmarkFolderPath(folderPath);
    const nextOverridesMap = new Map(bookmarkPreferences.folderOverrides.map((override) => [override.folderPath, override.openMode]));
    const normalizedOpenMode = normalizeBookmarkOverrideOpenMode(openMode);

    if (normalizedOpenMode === 'inherit') {
      nextOverridesMap.delete(normalizedFolderPath);
    } else {
      nextOverridesMap.set(normalizedFolderPath, normalizedOpenMode);
    }

    updateBookmarkPreferencesMutation.mutate({
      defaultOpenMode: bookmarkPreferences.defaultOpenMode,
      folderOverrides: [...nextOverridesMap.entries()].map(([nextFolderPath, nextOpenMode]) => ({
        folderPath: nextFolderPath,
        openMode: nextOpenMode
      }))
    });
  };

  const handleUpdateBookmarkItemOpenMode = (bookmarkId, openMode) => {
    updateBookmarkItemPreferenceMutation.mutate({
      bookmarkId,
      openMode: normalizeBookmarkOverrideOpenMode(openMode)
    });
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: FiUser },
    { id: 'home', name: 'Home Page', icon: FiHome },
    { id: 'notifications', name: 'Notifications', icon: FiBell },
    { id: 'connections', name: 'Connections', icon: FiCalendar },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/futurenote-icon.svg" alt="FutureNote" className="h-9 w-9" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">Settings</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                Dashboard
              </Link>
              
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiLogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>

            <div className="mt-6 rounded-lg border border-gray-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Build</p>
              <div className="mt-2 space-y-1 text-xs text-gray-600">
                <div className="flex items-center justify-between gap-3">
                  <span>UI</span>
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700">
                    {UI_BUILD_INFO.commit || 'unknown'}
                  </code>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>BE</span>
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700">
                    {buildInfo?.commit || 'loading'}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Profile Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <div className="mt-1 flex items-center space-x-3">
                      <p className="text-sm text-gray-900">{user?.phoneNumber}</p>
                      <button
                        onClick={() => {
                          const newPhone = prompt('Enter new phone number:');
                          if (newPhone) {
                            updatePhoneMutation.mutate(newPhone);
                          }
                        }}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'home' && (
              <div className="space-y-6">
                <div className="card">
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Home Page</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Widget visibility and order are saved to your FutureNote account.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {dashboardWidgetOrder.map((widgetId, index) => {
                      const isHidden = hiddenDashboardWidgets.includes(widgetId);
                      const isFirst = index === 0;
                      const isLast = index === dashboardWidgetOrder.length - 1;

                      return (
                        <div
                          key={widgetId}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {DASHBOARD_WIDGET_LABELS[widgetId] || widgetId}
                            </p>
                            <p className="text-xs text-gray-500">
                              {isHidden ? 'Hidden on the home page' : `Visible - position ${index + 1}`}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleMoveWidget(widgetId, 'up')}
                              disabled={isFirst}
                              className="rounded-full border border-gray-200 bg-white p-2 text-gray-400 transition hover:border-gray-300 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                              title="Move up"
                            >
                              <FiArrowUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveWidget(widgetId, 'down')}
                              disabled={isLast}
                              className="rounded-full border border-gray-200 bg-white p-2 text-gray-400 transition hover:border-gray-300 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                              title="Move down"
                            >
                              <FiArrowDown className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSetWidgetVisibility(widgetId, isHidden)}
                              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-gray-300 hover:text-gray-800"
                            >
                              {isHidden ? <FiEye className="h-3.5 w-3.5" /> : <FiEyeOff className="h-3.5 w-3.5" />}
                              {isHidden ? 'Show' : 'Hide'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="card">
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Bookmarks</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Control whether bookmarks open in the current tab or a new tab. Bookmark override falls back to folder override, then to the default.
                      </p>
                    </div>
                    {hiddenBookmarkFolderItems.length > 0 && (
                      <button
                        type="button"
                        onClick={handleShowAllHiddenBookmarkFolders}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-gray-300 hover:text-gray-800"
                      >
                        Show all hidden
                      </button>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default open behavior
                    </label>
                    <select
                      value={bookmarkPreferences.defaultOpenMode || BOOKMARK_DEFAULT_OPEN_MODE}
                      onChange={(event) => handleUpdateBookmarkDefaultOpenMode(event.target.value)}
                      className="input-field max-w-xs"
                      disabled={updateBookmarkPreferencesMutation.isLoading}
                    >
                      <option value="same_tab">Open in current tab</option>
                      <option value="new_tab">Open in new tab</option>
                    </select>
                  </div>

                  <div className="mt-4 rounded-xl border border-gray-200 bg-white">
                    <div className="border-b border-gray-200 px-4 py-3">
                      <h4 className="text-sm font-semibold text-gray-900">Folder behavior</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        Use a folder override only when it should differ from the default.
                      </p>
                    </div>

                    {allBookmarkFolderItems.length === 0 ? (
                      <div className="px-4 py-5 text-sm text-gray-500">No bookmark folders available yet.</div>
                    ) : (
                      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                        {allBookmarkFolderItems.map((item) => (
                          <div key={item.folderPath} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <FiFolder className="h-4 w-4 text-amber-500" />
                                <p className="truncate text-sm font-medium text-gray-900">{item.label}</p>
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                  {item.count}
                                </span>
                              </div>
                              <p className="mt-1 truncate text-xs text-gray-500" title={item.pathLabel}>
                                {item.pathLabel}
                              </p>
                            </div>
                            <select
                              value={item.openMode}
                              onChange={(event) => handleUpdateBookmarkFolderOpenMode(item.folderPath, event.target.value)}
                              className="input-field w-48 text-sm"
                              disabled={updateBookmarkPreferencesMutation.isLoading}
                            >
                              <option value="inherit">Use default</option>
                              <option value="same_tab">Open in current tab</option>
                              <option value="new_tab">Open in new tab</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 rounded-xl border border-gray-200 bg-white">
                    <div className="border-b border-gray-200 px-4 py-3">
                      <h4 className="text-sm font-semibold text-gray-900">Bookmark behavior</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        Per-bookmark override wins. If unset, the folder override is used. If the folder is unset, the default is used.
                      </p>
                    </div>

                    {bookmarkPreferenceItems.length === 0 ? (
                      <div className="px-4 py-5 text-sm text-gray-500">No bookmarks synced yet.</div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                        {bookmarkPreferenceItems.map((bookmark) => (
                          <div key={bookmark.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">{bookmark.title}</p>
                              <p className="mt-1 truncate text-xs text-gray-500" title={`${bookmark.folderPath} - ${bookmark.url}`}>
                                {bookmark.folderPath} · {bookmark.url}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <select
                                value={bookmark.bookmarkOpenMode}
                                onChange={(event) => handleUpdateBookmarkItemOpenMode(bookmark.id, event.target.value)}
                                className="input-field w-52 text-sm"
                                disabled={updateBookmarkItemPreferenceMutation.isLoading}
                              >
                                <option value="inherit">
                                  {bookmark.folderOpenMode === 'inherit' ? 'Use folder/default' : 'Use folder override'}
                                </option>
                                <option value="same_tab">Open in current tab</option>
                                <option value="new_tab">Open in new tab</option>
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    {hiddenBookmarkFolderItems.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500">
                      No bookmark folders are hidden right now.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {hiddenBookmarkFolderItems.map((item) => (
                          <div
                            key={item.folderPath}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <FiFolder className="h-4 w-4 text-amber-500" />
                                <p className="truncate text-sm font-medium text-gray-900">{item.label}</p>
                                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                  {item.count}
                                </span>
                              </div>
                              <p className="mt-1 truncate text-xs text-gray-500" title={item.pathLabel}>
                                {item.pathLabel}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleShowHiddenBookmarkFolder(item.folderPath)}
                              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-gray-300 hover:text-gray-800"
                            >
                              <FiEye className="h-3.5 w-3.5" />
                              Show folder
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Notification Preferences</h2>
                  
                  {prefsLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Notification Type
                        </label>
                        <select
                          value={preferenceForm?.defaultType || 'call'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPreferenceForm((prev) => {
                              const next = {
                                ...(prev || {}),
                                defaultType: value
                              };
                              updatePreferencesMutation.mutate(next);
                              return next;
                            });
                          }}
                          className="input-field"
                        >
                          <option value="call">Phone Call</option>
                          <option value="sms">SMS</option>
                          <option value="both">Both</option>
                      <option value="none">None</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Time (minutes before event)
                    </label>
                        <input
                          type="number"
                          min="1"
                          max="120"
                          value={preferenceForm?.defaultTime ?? 15}
                          onChange={(e) => {
                            const minutes = Math.max(1, parseInt(e.target.value, 10) || 1);
                            setPreferenceForm((prev) => {
                              const next = {
                                ...(prev || {}),
                                defaultTime: minutes
                              };
                              updatePreferencesMutation.mutate(next);
                              return next;
                            });
                          }}
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timezone
                        </label>
                        <select
                          value={preferenceForm?.timezone || 'UTC'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPreferenceForm((prev) => {
                              const next = {
                                ...(prev || {}),
                                timezone: value
                              };
                              updatePreferencesMutation.mutate(next);
                              return next;
                            });
                          }}
                          className="input-field"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                          <option value="Europe/London">Western Europe (London)</option>
                          <option value="Europe/Berlin">Central Europe (Berlin)</option>
                          <option value="Asia/Jerusalem">Israel Standard Time (Jerusalem)</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Quiet Hours
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={preferenceForm?.quietHours?.enabled || false}
                            onChange={(e) => handleQuietHoursChange({ enabled: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">
                            Pause reminders during a daily do-not-disturb window
                          </span>
                        </div>
                        {preferenceForm?.quietHours?.enabled && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm text-gray-700 mb-1">Start</label>
                              <input
                                type="time"
                                value={preferenceForm?.quietHours?.start || '22:00'}
                                onChange={(e) => handleQuietHoursChange({ enabled: true, start: e.target.value })}
                                className="input-field"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-700 mb-1">End</label>
                              <input
                                type="time"
                                value={preferenceForm?.quietHours?.end || '07:00'}
                                onChange={(e) => handleQuietHoursChange({ enabled: true, end: e.target.value })}
                                className="input-field"
                              />
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          If a reminder falls inside quiet hours, FutureNote sends it after the window ends only if the meeting is still active.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Pre-call SMS Reminder
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={preferenceForm?.preCallSms?.enabled || false}
                            onChange={(e) => {
                              const enable = e.target.checked;
                              setPreferenceForm((prev) => {
                                const prevPreCall = prev?.preCallSms || {};
                                const nextPreCall = {
                                  enabled: enable,
                                  minutesBeforeCall: prevPreCall.minutesBeforeCall ?? 15
                                };
                                const next = {
                                  ...(prev || {}),
                                  preCallSms: nextPreCall
                                };
                                updatePreferencesMutation.mutate(next);
                                return next;
                              });
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">
                            Send an SMS reminder before the call notification
                          </span>
                        </div>
                        {preferenceForm?.preCallSms?.enabled && (
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700">Minutes before call:</label>
                            <input
                              type="number"
                              min="1"
                              max="240"
                              value={preferenceForm?.preCallSms?.minutesBeforeCall ?? 15}
                              onChange={(e) => {
                                const minutes = Math.max(1, parseInt(e.target.value, 10) || 1);
                                setPreferenceForm((prev) => {
                                  const next = {
                                    ...(prev || {}),
                                    preCallSms: {
                                      enabled: true,
                                      minutesBeforeCall: minutes
                                    }
                                  };
                                  updatePreferencesMutation.mutate(next);
                                  return next;
                                });
                              }}
                              className="input-field w-24"
                            />
                          </div>
                        )}
                  </div>
                </div>
              )}
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Notifications</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Send a test notification to verify your settings are working correctly.
                  </p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleTestNotification('call')}
                      disabled={testNotificationMutation.isLoading}
                      className="btn-primary"
                    >
                      Test Call
                    </button>
                    <button
                      onClick={() => handleTestNotification('sms')}
                      disabled={testNotificationMutation.isLoading}
                      className="btn-secondary"
                    >
                      Test SMS
                    </button>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Resync Events</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Reload calendar events and reset notification timing to your current defaults.
                  </p>
                  <button
                    onClick={() => resyncAllMutation.mutate()}
                    disabled={resyncAllMutation.isLoading}
                    className="btn-secondary flex items-center"
                  >
                    <FiRefreshCw className="h-4 w-4 mr-2" />
                    {resyncAllMutation.isLoading ? 'Resyncing...' : 'Resync all'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'connections' && (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Provider Connections</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Connect calendar and task access separately for each Google or Microsoft account.
                </p>
                <div className="mb-6 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Calendar and tasks now use separate consent flows. Connect only the capability you want to use.
                </div>

                <div className="space-y-6">
                  {[{
                    provider: 'google',
                    label: 'Google',
                    accent: 'bg-red-500'
                  }, {
                    provider: 'microsoft',
                    label: 'Microsoft',
                    accent: 'bg-blue-500'
                  }].map(({ provider, label, accent }) => {
                    const providerAccounts = accountsByProvider[provider] || [];
                    const calendarCount = providerAccounts.filter((account) => account.capabilities?.calendar).length;
                    const taskCount = providerAccounts.filter((account) => account.capabilities?.tasks).length;

                    return (
                      <div key={provider} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-3 ${providerAccounts.length ? 'bg-green-500' : accent}`}></div>
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">{label}</h3>
                              <p className="text-sm text-gray-500">
                                {accountsLoading ? 'Loading accounts...' : `${providerAccounts.length} linked · ${calendarCount} calendar · ${taskCount} tasks`}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              onClick={() => linkProvider(provider, 'calendar')}
                              className="btn-primary text-sm px-3 py-1"
                            >
                              <span className="inline-flex items-center gap-1">
                                <FiCalendar className="h-4 w-4" />
                                Connect calendar
                              </span>
                            </button>
                            <button
                              onClick={() => linkProvider(provider, 'tasks')}
                              className="btn-secondary text-sm px-3 py-1"
                            >
                              <span className="inline-flex items-center gap-1">
                                <FiCheckSquare className="h-4 w-4" />
                                Connect tasks
                              </span>
                            </button>
                          </div>
                        </div>

                        {accountsLoading ? null : (
                          <div className="mt-4 space-y-2">
                            {providerAccounts.length === 0 ? (
                              <p className="text-sm text-gray-500">No accounts connected yet.</p>
                            ) : (
                              providerAccounts.map((account) => {
                                const lastSync = account.lastSync ? new Date(account.lastSync).toLocaleString() : 'Never';
                                const displayLabel = account.email || account.displayName || 'Unknown account';
                                return (
                                  <div key={account.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{displayLabel}</p>
                                      <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${account.capabilities?.calendar ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                                          calendar
                                        </span>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${account.capabilities?.tasks ? 'bg-sky-100 text-sky-700' : 'bg-gray-200 text-gray-500'}`}>
                                          tasks
                                        </span>
                                      </div>
                                      <p className="mt-1 text-xs text-gray-500">Last sync: {lastSync}</p>
                                    </div>
                                    <button
                                      onClick={() => handleDisconnectOAuth(account.id, displayLabel)}
                                      disabled={disconnectOAuthMutation.isLoading}
                                      className="btn-danger text-xs px-3 py-1"
                                    >
                                      Disconnect
                                    </button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;


